import React from 'react';
import {
  BranchNodeData,
  BRANCH_FIELD_OPTIONS,
  PRIORITY_OPTIONS,
  STATUS_OPTIONS,
  CHANNEL_OPTIONS,
  EXISTENCE_OPTIONS,
} from '../types';
import { useOrgTicketTypes } from '../hooks/useOrgData';
import { ThemedSelect } from '@components/common/ThemedSelect';
import { FieldValueSelect } from './FieldValueSelect';

interface Props {
  data: BranchNodeData;
  onChange: (updated: Partial<BranchNodeData>) => void;
}

export function BranchConfig({ data, onChange }: Props) {
  const { ticketTypes, loading } = useOrgTicketTypes();

  const selectedField = BRANCH_FIELD_OPTIONS.find((f) => f.value === data.field);
  const valueType = selectedField?.valueType ?? 'text';

  const handleFieldChange = (field: string) => {
    const fieldOption = BRANCH_FIELD_OPTIONS.find((f) => f.value === field);
    if (fieldOption?.valueType === 'existence') {
      // For existence checks, set operator and clear value
      onChange({ field, operator: 'is_not_empty', value: '', displayLabel: `${fieldOption.label} exists` });
    } else {
      onChange({ field, operator: 'equals', value: '', displayLabel: undefined });
    }
  };

  const buildDisplayLabel = (field: string, value: string, operator?: string): string => {
    const fieldOption = BRANCH_FIELD_OPTIONS.find((f) => f.value === field);
    const fieldLabel = fieldOption?.label ?? field;

    // Handle existence checks
    if (fieldOption?.valueType === 'existence') {
      const existsLabel = operator === 'is_empty' ? 'does not exist' : 'exists';
      return `${fieldLabel} ${existsLabel}`;
    }

    let valueLabel = value;
    if (field === 'entity.ticketTypeId') {
      valueLabel = ticketTypes.find((t) => t.id === value)?.name ?? value;
    } else if (field === 'entity.priority') {
      valueLabel = PRIORITY_OPTIONS.find((p) => p.value === value)?.label ?? value;
    } else if (field === 'entity.status') {
      valueLabel = STATUS_OPTIONS.find((s) => s.value === value)?.label ?? value;
    } else if (field === 'entity.channel') {
      valueLabel = CHANNEL_OPTIONS.find((c) => c.value === value)?.label ?? value;
    }
    return `${fieldLabel} is ${valueLabel}`;
  };

  const handleValueChange = (value: string) => {
    onChange({
      value,
      displayLabel: buildDisplayLabel(data.field, value, data.operator),
    });
  };

  const handleOperatorChange = (operator: string) => {
    onChange({
      operator,
      displayLabel: buildDisplayLabel(data.field, data.value, operator),
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">If this field</label>
        <ThemedSelect
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          value={data.field}
          onChange={(e) => handleFieldChange(e.target.value)}
        >
          <option value="">Select a field…</option>
          {BRANCH_FIELD_OPTIONS.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </ThemedSelect>
      </div>

      {valueType === 'existence' ? (
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">Check</label>
          <ThemedSelect
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            value={data.operator}
            onChange={(e) => handleOperatorChange(e.target.value)}
          >
            {EXISTENCE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </ThemedSelect>
        </div>
      ) : (
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">Is equal to</label>
        <FieldValueSelect
          valueType={valueType}
          value={data.value}
          ariaLabel="Is equal to"
          onChange={(value, label) =>
            onChange({
              value,
              // Stage (and ticket type) names come from the picker; the rest
              // are resolved by buildDisplayLabel.
              displayLabel:
                valueType === 'stage'
                  ? `${selectedField?.label ?? data.field} is ${label}`
                  : buildDisplayLabel(data.field, value, data.operator),
            })
          }
        />
      </div>
      )}
    </div>
  );
}
